import { html } from "htm/preact";

/**
 * The app's icon set, drawn inline.
 *
 * These used to be emoji. Emoji can't be tinted, render at a different weight on
 * every platform, and turn into a coloured blob at tab-bar size — which matters
 * here because the tab bar is the app's main navigation. One monoline set on a
 * 24-unit grid, stroked in currentColor, keeps every screen consistent.
 */
const STROKE: Record<string, string> = {
  flag: "M6 21V4m0 0h11l-2.6 3.5L17 11H6",
  trophy: "M7 4h10v5.5a5 5 0 0 1-10 0zM7 6H4v1a3.5 3.5 0 0 0 3 3.4M17 6h3v1a3.5 3.5 0 0 1-3 3.4M12 14.5V18M8 21h8",
  sliders: "M4 7h16M4 12h16M4 17h16",
  share: "M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6M12 3v13M7.5 7.5 12 3l4.5 4.5",
  link: "M9.5 14.5 15 9M10.5 6.6l1.8-1.8a4 4 0 0 1 5.7 5.7l-1.8 1.8M13.5 17.4l-1.8 1.8a4 4 0 0 1-5.7-5.7l1.8-1.8",
  home: "M3.5 10.5 12 3.5l8.5 7V20a1 1 0 0 1-1 1H15v-6H9v6H4.5a1 1 0 0 1-1-1z",
  sun: "M12 2.5v2M12 19.5v2M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M2.5 12h2M19.5 12h2M4.9 19.1l1.5-1.5M17.6 6.4l1.5-1.5",
  moon: "M20.5 14.8A8.5 8.5 0 0 1 9.2 3.5a8.5 8.5 0 1 0 11.3 11.3z",
  left: "M15 4.5 7.5 12l7.5 7.5",
  right: "M9 4.5 16.5 12 9 19.5",
  down: "M5 9l7 7 7-7",
  // Full arrows, not the chevrons above: these mark a change in the standings, and
  // need to read as direction of travel at 14px rather than as an affordance.
  "arrow-up": "M12 20V5M5.5 11.5 12 5l6.5 6.5",
  "arrow-down": "M12 4v15M5.5 12.5 12 19l6.5-6.5",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  close: "M6 6l12 12M18 6 6 18",
  check: "M4 12.5 9.5 18 20 6",
  user: "M4.5 20.5a7.5 7.5 0 0 1 15 0",
  clock: "M12 7.5V12l3 2",
  scorecard: "M3.5 4.5h17v15h-17zM3.5 10h17M10 4.5v15",
  book: "M12 7C10.4 5.4 8.2 4.8 3.5 4.8v13c4.7 0 6.9.6 8.5 2.2 1.6-1.6 3.8-2.2 8.5-2.2v-13C15.8 4.8 13.6 5.4 12 7zM12 7v13",
  shield: "M12 3l7.5 3v5.6c0 4.6-3.1 8.2-7.5 9.9-4.4-1.7-7.5-5.3-7.5-9.9V6z",
  download: "M12 3.5v11M7.5 10 12 14.5 16.5 10M4.5 20.5h15",
  copy: "M9 8.5h10.5V20H9zM5 15.5V4h10.5",
  search: "M16.2 16.2 21 21",
  calendar: "M4 6h16v14.5H4zM4 10.5h16M8.5 3.5V7M15.5 3.5V7",
  alert: "M12 9v4.5M12 16.8v.2M12 3.6 21.3 20H2.7z",
  // Distance to the green — a dropped pin, the one place the app talks about a spot
  // on the ground rather than a number on a card.
  pin: "M12 21.5s7-6.9 7-11.5a7 7 0 1 0-14 0c0 4.6 7 11.5 7 11.5z",
  // The gallery: watching rather than playing.
  eye: "M2.5 12S6.5 5.5 12 5.5 21.5 12 21.5 12 17.5 18.5 12 18.5 2.5 12 2.5 12z",
  // Clubhouse mode's own controls, which have no app bar to live in.
  play: "M8 5.5 18.5 12 8 18.5z",
  pause: "M9 5.5v13M15 5.5v13",
  expand: "M4 9V4h5M20 15v5h-5M15 4h5v5M9 20H4v-5",
};

/** Circles are separate so the round parts stay true circles at any size. */
const CIRCLES: Record<string, [number, number, number][]> = {
  sun: [[12, 12, 4]],
  user: [[12, 8, 3.6]],
  clock: [[12, 12, 8.5]],
  search: [[10.5, 10.5, 6.5]],
  eye: [[12, 12, 3.2]],
  pin: [[12, 10, 2.5]],
  sliders: [
    [9, 7, 2.2],
    [15, 12, 2.2],
    [7, 17, 2.2],
  ],
};

export type IconName = keyof typeof STROKE;

export function Icon({
  name,
  size = 20,
  class: className = "icon",
}: {
  name: IconName;
  size?: number;
  class?: string;
}) {
  return html`
    <svg
      class=${className}
      width=${size}
      height=${size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d=${STROKE[name]} />
      ${(CIRCLES[name] ?? []).map(
        ([cx, cy, r]) => html`<circle key=${`${cx}-${cy}`} cx=${cx} cy=${cy} r=${r} />`,
      )}
    </svg>
  `;
}
